import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { VcsTrustEntry } from '@/lib/apiTypes'
import { useVcsTrust } from '@/lib/hooks'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/format'
import { RepoProviderIcon } from '@/shared/components/RepoProviderIcon'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonTable } from '@/shared/components/Skeleton'
import { FeedbackMessage } from '@/shared/components/FeedbackMessage'
import { useToast } from '@/context/ToastContext'
import { Tooltip } from '@/shared/components/Tooltip'
import { AddVcsTrustModal } from '@/features/org/components/AddVcsTrustModal'
import { EditAllowedRefsModal } from '@/features/org/components/EditAllowedRefsModal'

/** The refs cell: the list as mono chips, or the two states that are not a list. */
function AllowedRefsCell({ refs }: { refs: string[] | null }) {
  if (refs === null) {
    return (
      <Tooltip text="Created before ref rules existed: this pipeline can push from any branch, tag or pull request. Edit to tighten." placement="top">
        <span tabIndex={0} className="badge badge-amber badge-still cursor-help">Any ref</span>
      </Tooltip>
    )
  }
  if (refs.length === 0) {
    return (
      <Tooltip text="The stored rule could not be parsed, so every push from this entry is refused until it is re-saved." placement="top">
        <span tabIndex={0} className="badge badge-red cursor-help">Invalid rule</span>
      </Tooltip>
    )
  }
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {refs.map((r) => (
        <span key={r} className="badge badge-neutral" style={{ fontFamily: 'var(--mono)', fontSize: '0.7rem', textTransform: 'none' }}>{r}</span>
      ))}
    </span>
  )
}

export function VcsTrustCard({ orgId, canAdmin }: { orgId: string; canAdmin: boolean }) {
  const trust = useVcsTrust(orgId)
  const { showToast } = useToast()
  const [showAddModal, setShowAddModal] = useState(false)
  const [editing, setEditing]           = useState<VcsTrustEntry | null>(null)
  const [deletingId, setDeletingId]     = useState<string | null>(null)

  async function handleDelete(entry: VcsTrustEntry) {
    setDeletingId(entry.id)
    try {
      await api.delete(`/api/orgs/${orgId}/vcs-trust/${entry.id}`)
      trust.refetch()
      showToast('Trust entry removed.', 'success')
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setDeletingId(null)
    }
  }

  const legacyCount = trust.data?.entries.filter((e) => e.allowed_refs === null).length ?? 0

  return (
    <>
      {showAddModal && (
        <AddVcsTrustModal
          orgId={orgId}
          onClose={() => setShowAddModal(false)}
          onAdded={() => { trust.refetch(); showToast('Trust entry added.', 'success') }}
        />
      )}
      {editing && (
        <EditAllowedRefsModal
          orgId={orgId}
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { trust.refetch(); showToast('Allowed refs updated.', 'success') }}
        />
      )}

      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <p style={{ fontFamily: 'var(--body)', fontSize: '0.82rem', color: 'var(--text2)', margin: 0 }}>
            Trusted repos can authenticate directly using native OIDC tokens from GitHub Actions or GitLab CI - no stored secrets needed.
            Each entry also says which branches may push; reads, locks and plans work from any branch.
            See the <Link to="/integrations/oidc-trust" style={{ color: 'var(--accent)' }}>Pipeline Trust guide</Link> for details.
          </p>
          {canAdmin && (
            <button className="btn btn-primary btn-sm" style={{ marginLeft: 16, flexShrink: 0 }} onClick={() => setShowAddModal(true)}>
              + Add trust
            </button>
          )}
        </div>

        {canAdmin && legacyCount > 0 && (
          <p style={{ fontSize: '0.78rem', color: 'var(--amber)', marginTop: 0, marginBottom: 12 }}>
            {legacyCount === 1 ? 'One entry allows' : `${legacyCount} entries allow`} pushes from any ref. They were created before branch rules
            existed; edit each to restrict it to the branches that should land changes.
          </p>
        )}

        {trust.loading && (
          <SkeletonTable headers={['Provider', 'Subject', 'Allowed refs', 'Added', ...(canAdmin ? [''] : [])]} firstColTwoLines={false} rows={4} />
        )}

        {!trust.loading && trust.error && (
          trust.error.toLowerCase().includes('forbidden') || trust.error.toLowerCase().includes('not authorized') || trust.error.toLowerCase().includes('permission')
            ? <EmptyState icon="🔒" title="Access restricted" subtitle="You don't have permission to view Pipeline Trust entries. Ask an org owner or admin to grant you access." />
            : <FeedbackMessage ok={false} msg={trust.error} />
        )}

        {!trust.loading && !trust.error && (trust.data?.entries.length ?? 0) > 0 && (
          <div className="table-wrap fu">
            <table>
              <thead>
                <tr>
                  <th><Tooltip text="The CI provider whose OIDC tokens this entry trusts." placement="top"><span tabIndex={0} className="cursor-help">Provider</span></Tooltip></th>
                  <th><Tooltip text="OIDC subject claim that must match for a pipeline to authenticate." placement="top"><span tabIndex={0} className="cursor-help">Subject</span></Tooltip></th>
                  <th><Tooltip text="Branches (and tag: globs) this pipeline may push state, lock files and modules from. Reads, locks and plans are not restricted." placement="top"><span tabIndex={0} className="cursor-help">Allowed refs</span></Tooltip></th>
                  <th><Tooltip text="When this trust entry was added." placement="top"><span tabIndex={0} className="cursor-help">Added</span></Tooltip></th>
                  {canAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {trust.data!.entries.map((e) => (
                  <tr key={e.id}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <RepoProviderIcon provider={e.provider} size={16} />
                        {e.provider === 'github' ? 'GitHub' : e.provider === 'gitlab' ? 'GitLab' : e.provider}
                      </span>
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem' }}>{e.subject}</td>
                    <td><AllowedRefsCell refs={e.allowed_refs} /></td>
                    <td style={{ color: 'var(--text2)', fontSize: '0.8rem' }}>{formatDate(e.created_at)}</td>
                    {canAdmin && (
                      <td>
                        <span style={{ display: 'inline-flex', gap: 6 }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            disabled={deletingId !== null}
                            onClick={() => setEditing(e)}
                          >
                            Edit refs
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={deletingId !== null}
                            onClick={() => { void handleDelete(e) }}
                          >
                            {deletingId === e.id ? '…' : 'Remove'}
                          </button>
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!trust.loading && !trust.error && (trust.data?.entries.length ?? 0) === 0 && (
          <div className="empty-state fu">
            <div>No trusted repos yet.</div>
          </div>
        )}
      </div>
    </>
  )
}
